import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import NotificationProvider from './components/NotificationToast';
import GlobalSearch from './components/GlobalSearch';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';
  
// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Accounts from './pages/Accounts';
import MyChannels from './pages/MyChannels';
import AccountNew from './pages/AccountNew';
import AccountNewManual from './pages/AccountNewManual';
import AccountDetail from './pages/AccountDetail';
import Groups from './pages/Groups';
import GroupDetail from './pages/GroupDetail';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Blacklist from './pages/Blacklist';
import Sessions from './pages/Sessions';
import SessionDetail from './pages/SessionDetail';
import Metrics from './pages/Metrics';
import Instructions from './pages/Instructions';
import AboutProduct from './pages/AboutProduct';
import Processes from './pages/Processes';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import AdminUsers from './pages/AdminUsers';
import AgentsGlobal from './pages/AgentsGlobal';
import AgentDetailPage from './pages/AgentDetailPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchInitialMode, setSearchInitialMode] = useState('telegram-history');

  useEffect(() => {
    const openSearch = (mode = 'telegram-history') => {
      setSearchInitialMode(mode);
      setIsSearchOpen(true);
    };

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        openSearch('telegram-history');
      }
    };

    const handleOpenGlobalSearch = (e) => {
      openSearch(e.detail?.mode || 'telegram-history');
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('open-global-search', handleOpenGlobalSearch);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('open-global-search', handleOpenGlobalSearch);
    };
  }, []);

  return (
    <NotificationProvider>
      <GlobalSearch
        isOpen={isSearchOpen}
        initialMode={searchInitialMode}
        onClose={() => setIsSearchOpen(false)}
      />

      <Routes>
      {/* Auth Routes */}
      <Route path="/login" element={
        <AuthLayout>
          <Login />
        </AuthLayout>
      } />

      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout>
            <Dashboard />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/my-channels" element={<ProtectedRoute><MainLayout><MyChannels /></MainLayout></ProtectedRoute>} />

      <Route path="/accounts" element={
        <ProtectedRoute>
          <MainLayout>
            <Accounts />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/accounts/new" element={
        <ProtectedRoute>
          <MainLayout>
            <AccountNew />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/accounts/new/manual" element={
        <ProtectedRoute>
          <MainLayout>
            <AccountNewManual />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/accounts/:id" element={
        <ProtectedRoute>
          <MainLayout>
            <AccountDetail />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/groups" element={
        <ProtectedRoute>
          <MainLayout>
            <Groups />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/groups/:id" element={
        <ProtectedRoute>
          <MainLayout>
            <GroupDetail />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/blacklist" element={
        <ProtectedRoute>
          <MainLayout>
            <Blacklist />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects" element={
        <ProtectedRoute>
          <MainLayout>
            <Projects />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/new" element={
        <ProtectedRoute>
          <MainLayout>
            <ProjectDetail />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:id" element={
        <ProtectedRoute>
          <MainLayout>
            <ProjectDetail />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/sessions" element={
        <ProtectedRoute>
          <MainLayout>
            <Sessions />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/sessions/:id" element={
        <ProtectedRoute>
          <MainLayout>
            <SessionDetail />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/metrics" element={
        <ProtectedRoute>
          <MainLayout>
            <Metrics />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/instructions" element={
        <ProtectedRoute>
          <MainLayout>
            <Instructions />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/about-product" element={
        <ProtectedRoute>
          <MainLayout>
            <AboutProduct />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/processes" element={
        <ProtectedRoute>
          <MainLayout>
            <Processes />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute>
          <MainLayout>
            <Settings />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/agents" element={
        <ProtectedRoute>
          <MainLayout>
            <AgentsGlobal />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/agents/:id" element={
        <ProtectedRoute>
          <MainLayout>
            <AgentDetailPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin-users" element={
        <ProtectedRoute>
          <MainLayout>
            <AdminUsers />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* 404 Route */}
      <Route path="*" element={
        <MainLayout>
          <NotFound />
        </MainLayout>
      } />
      </Routes>
    </NotificationProvider>
  );
}

export default App;
